/**
 * Resource policy — central dispatch for "is this resource a currency-bar
 * scalar or a backpack-stack item?"
 *
 * Lives in `shared/` so server (gather, craft, debit, drops) and client (HUD,
 * cost displays) consult identical routing. The 17-key policy table is the
 * single source of truth — flipping a key from `{ kind: "scalar" }` to
 * `{ kind: "item", itemId: ... }` in this file alone moves it between the
 * resource-bar HUD and the backpack with no other code edits, because every
 * consumer goes through `getMaterial` / `creditMaterial` / `consumeMaterial`
 * (server-side helpers in `game/server/src/items/material-flow.ts`).
 *
 * Per the v10 UI spec (`docs/superpowers/specs/2026-05-01-ui-implementation-spec.md`
 * §1) gold is the ONLY HUD currency-bar counter; every other resource —
 * lumber, stone, shadowstone, radiantGem included — reverts to KTS-canonical
 * inventory items in the 6×2 backpack. Earlier drafts kept lumber/stone on
 * a resource bar; the v10 plate has no resource bar, so those counters were
 * orphaned (gathered but invisible) until the 2026-05-03 flip below.
 *
 * Interconnections:
 *   - gather.ts: writes per-swing yield through `creditMaterial`
 *   - HelloRoom.ts wool drop: writes through `creditMaterial`
 *   - buildings/cost.ts: canAfford / debit / refund all dispatch via the helpers
 *   - items/craft.ts classifyCostKey: reads `RESOURCE_POLICY[key]?.kind`
 *   - bots/rules/ai-berries.ts + ai-herbs.ts: read supply via `getMaterial`
 *   - debug/actions.ts giveResource: routes via `creditMaterial`
 *   - hud/main-plate.ts: backpack-routed materials surface in real backpack
 *     slots (no separate materials grid)
 */
import type { ItemId } from "./items";

/** Where a resource key's value lives + how it's accessed.
 *  - "scalar": cube.resources[key] uint16 counter; displayed in resource bar.
 *  - "item": stackable backpack item with the given itemId; drops on death.
 *  Switching a key from "scalar" to "item" is a one-line flip — gather paths,
 *  costs, bot thresholds, HUD all consult this policy via the helpers. */
export type ResourcePolicy =
  | { readonly kind: "scalar" }
  | { readonly kind: "item"; readonly itemId: ItemId };

export const RESOURCE_POLICY: Readonly<Record<string, ResourcePolicy>> = {
  // Currency-style — resource bar HUD, no inventory cost, persists through death.
  // Gold is the only currency in v10. ResourceBag.gold remains uint16 with a
  // dedicated top-left HUD counter (`hud/gold-counter.ts`).
  gold:                    { kind: "scalar" },
  // Material-style — backpack stack, drops on death, costs an inventory slot.
  lumber:                  { kind: "item", itemId: "Lumber" },
  stone:                   { kind: "item", itemId: "Stone" },
  shadowstone:             { kind: "item", itemId: "Shadowstone" },
  radiantGem:              { kind: "item", itemId: "RadiantGem" },
  ironOre:                 { kind: "item", itemId: "IronOre" },
  leather:                 { kind: "item", itemId: "Leather" },
  bearPelt:                { kind: "item", itemId: "BearPelt" },
  wool:                    { kind: "item", itemId: "Wool" },
  infusedLumber:           { kind: "item", itemId: "InfusedLumber" },
  sageberry:               { kind: "item", itemId: "Sageberry" },
  spicyHerbs:              { kind: "item", itemId: "SpicyHerbs" },
  nightcrawler:            { kind: "item", itemId: "Nightcrawler" },
  fish:                    { kind: "item", itemId: "Fish" },
  ancientArtifactFragment: { kind: "item", itemId: "AncientArtifactFragment" },
  berry:                   { kind: "item", itemId: "Berry" },
  herb:                    { kind: "item", itemId: "Herb" },
};
